import type { RootState } from 'app'
import { createSelector } from '@reduxjs/toolkit'
import { VerifyStatus, ContractorDetails } from 'shared/types'
import { getCompanyDataState } from 'shared/store/users/selectors'
import { getContractorDetails } from 'shared/store/contractors/selectors'
import { getCountries } from 'shared/store/settings/selectors'
import {
  parseAddress,
  isAddressCorrect,
  transformBankAccountNumber,
} from 'shared/utils'

const getLawsuitsState = (state: RootState) => state.Lawsuits

export const getRecommendedInvoices = createSelector(
  getLawsuitsState,
  state => state.recommendedInvoices
)

export const getLawsuits = createSelector(
  getLawsuitsState,
  state => state.lawsuits
)

export const getSorting = createSelector(
  getLawsuitsState,
  state => state.sorting
)

export const getPowerOfAttorneyTemplateState = createSelector(
  getLawsuitsState,
  state => state.powerOfAttorneyTemplate
)

export const getPowerOfAttorneyTemplateUrl = createSelector(
  getPowerOfAttorneyTemplateState,
  state => (state.data ? window.URL.createObjectURL(state.data) : '#')
)

export const getUploadPowerOfAttorneyState = createSelector(
  getLawsuitsState,
  state => state.uploadPowerOfAttorney
)

export const getCircumstancesState = createSelector(
  getLawsuitsState,
  state => state.circumstances
)

export const getCircumstanceQuestionsState = createSelector(
  getCircumstancesState,
  state => state.questions
)

export const getDocumentsState = createSelector(
  getLawsuitsState,
  state => state.documents
)

export const getCircumstancesFormValues = createSelector(
  getCircumstancesState,
  state => state.formValues
)

export const getPartiesState = createSelector(
  getLawsuitsState,
  state => state.parties
)

export const getSummaryState = createSelector(
  getLawsuitsState,
  state => state.summary
)

export const getLawsuitPartiesData = createSelector(
  getCompanyDataState,
  getContractorDetails,
  (companyDataState, { data: contractorDetails }) => ({
    user: companyDataState,
    contractor: contractorDetails as ContractorDetails,
  })
)

export const getPartiesFormValues = createSelector(
  getPartiesState,
  state => state.formValues
)

export const getFileLawsuitState = createSelector(
  getLawsuitsState,
  state => state.fileLawsuit
)

export const getLawyerCases = createSelector(
  getLawsuitsState,
  state => state.lawyerLawsuits
)

export const getLawsuitDetails = createSelector(
  getLawsuitsState,
  getCountries,
  (state, countries) => {
    const { data, ...rest } = state.lawsuitDetails
    const {
      bankAccountNumber,
      countryId,
      contact: { emailAddress, ...phone },
      name,
      taxNumber,
      ...addressInfo
    } = data.client
    const {
      name: contractorName,
      bankAccountNumber: contractorBankAccountNumber,
      countryId: contractorCountryId,
      contact: { emailAddress: contractorEmailAddress, ...contractorPhone },
      taxNumber: contractorTaxNumber,
      ...contractorAddresInfo
    } = data.contractor

    const phonePrefix =
      (phone.countryId &&
        countries?.find(country => country.id === phone.countryId)
          ?.phonePrefix) ||
      ''

    const contractorPhonePrefix =
      (phone.countryId &&
        countries?.find(country => country.id === contractorPhone.countryId)
          ?.phonePrefix) ||
      ''

    return {
      ...rest,
      data: {
        ...data,
        client: {
          name,
          taxNumber,
          address: isAddressCorrect(addressInfo)
            ? parseAddress(addressInfo)
            : '-',
          country:
            countries?.find(country => country.id === countryId)?.name || '',
          email: emailAddress,
          phoneNumber: phone.phoneNumber
            ? `${phonePrefix} ${phone.phoneNumber}`.trim()
            : '',
          bankAccountNumber: transformBankAccountNumber(
            bankAccountNumber || ''
          ),
        },
        contractor: {
          name: contractorName,
          taxNumber: contractorTaxNumber,
          address: isAddressCorrect(contractorAddresInfo)
            ? parseAddress(contractorAddresInfo)
            : '-',
          country:
            countries?.find(country => country.id === contractorCountryId)
              ?.name || '',
          email: contractorEmailAddress,
          phoneNumber: contractorPhone.phoneNumber
            ? `${contractorPhonePrefix} ${contractorPhone.phoneNumber}`.trim()
            : '',
          bankAccountNumber: transformBankAccountNumber(
            contractorBankAccountNumber || ''
          ),
        },
      },
    }
  }
)

export const getFilesAndAnswersToCorrect = createSelector(
  getLawsuitDetails,
  ({ data: { files: lawsuitFiles, answers: lawsuitAnswers } }) => {
    const files = lawsuitFiles.filter(
      ({ checkStatus }) => checkStatus === VerifyStatus.Rejected
    )
    const answers = lawsuitAnswers.filter(
      ({ checkStatus }) => checkStatus === VerifyStatus.Rejected
    )
    return {
      files,
      answers,
    }
  }
)

export const getLawsuitMessages = createSelector(
  getLawsuitsState,
  state => state.lawsuitMessages
)

export const getVerificationFormValues = createSelector(
  getLawsuitsState,
  state => state.verificationFormValues
)

export const getDataCorrectionData = createSelector(
  getLawsuitsState,
  state => state.dataCorrection
)

export const getDataCorrectionFiles = createSelector(
  getDataCorrectionData,
  state => state.data.files
)

export const getVerifyLawsuitState = createSelector(
  getLawsuitsState,
  state => state.verifyLawsuitData
)

export const getSetBankTransferInfoState = createSelector(
  getLawsuitsState,
  state => state.setBankTransferInfo
)

export const getFilesAndAnswersToVerify = createSelector(
  getLawsuitDetails,
  ({ data: { files: lawsuitFiles, answers: lawsuitAnswers } }) => {
    const files = lawsuitFiles.filter(
      ({ checkStatus }) => checkStatus === VerifyStatus.NotVerified
    )
    const answers = lawsuitAnswers.filter(
      ({ checkStatus }) => checkStatus === VerifyStatus.NotVerified
    )
    return {
      files,
      answers,
    }
  }
)

export const getBankTransfersState = createSelector(
  getLawsuitsState,
  state => state.bankTransfers
)

export const getUploadBankTransferConfirmationState = createSelector(
  getLawsuitsState,
  state => state.uploadBankTransferConfirmation
)

export const getBankTransferFilesState = createSelector(
  getLawsuitsState,
  state => state.bankTransferFiles
)

export const getVerifyBankTransferFilesState = createSelector(
  getLawsuitsState,
  state => state.verifyBankTransferFiles
)

export const getLawsuitHyperlinks = createSelector(
  getLawsuitsState,
  state => state.hyperlinks
)

export const getFiledLawsuitFiles = createSelector(
  getLawsuitsState,
  state => state.filedLawsuitFiles
)

export const getUploadFiledLawsuitFilesState = createSelector(
  getLawsuitsState,
  state => state.uploadFiledLawsuitFiles
)

export const getUpdateLawsuitFiles = createSelector(
  getLawsuitsState,
  state => state.updateLawsuitFiles
)

export const getUpdateLawsuitState = createSelector(
  getLawsuitsState,
  state => state.updateLawsuit
)

export const getSendMessageFiles = createSelector(
  getLawsuitsState,
  state => state.sendMessageFiles
)

export const getSendMessageState = createSelector(
  getLawsuitsState,
  state => state.sendMessage
)

export const getMarkLawsuitMessagesAsReadState = createSelector(
  getLawsuitsState,
  state => state.markLawsuitMessagesAsRead
)

export const getWithdrawLawsuitState = createSelector(
  getLawsuitsState,
  state => state.withdrawLawsuit
)

export const getCustomerServiceState = createSelector(
  getLawsuitsState,
  state => state.customerService
)

export const getSendLawsuitWithdrawalMessageState = createSelector(
  getLawsuitsState,
  state => state.sendLawsuitWithdrawalMessage
)

export const getWithdrawPowerOfAttorneyState = createSelector(
  getLawsuitsState,
  state => state.withdrawPowerOfAttorney
)

export const getPowerOfAttorneyState = createSelector(
  getLawsuitsState,
  state => state.powerOfAttorney
)
